import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { AxiosResponse } from "axios";
import { ProgressSpinner } from "primereact/progressspinner";
import { useTranslation } from "react-i18next";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { Warehouse } from "../../models/Warehouse";
import { Product } from "../../models/Product";
import { Utility } from "../../core/Utility";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import "./InventoryCountListPage.scss";

type InventoryType = "mercantile" | "repromaterial";

type ExcelRow = {
  SifraRobe: string;
  Naziv: string;
  Oznaka: string;
  Kolicina: number;
  Srps?: number;
};

export const InventoryCountListPage = () => {
  const { t } = useTranslation();
  const { state } = useApplicationContext();

  const currentYear = new Date().getFullYear();
  const initialYear = (state.businessYear ?? currentYear) - 1;

  const [businessYear, setBusinessYear] = useState<string>(String(initialYear));

  const businessYearNum = useMemo(
    () => parseInt(businessYear, 10),
    [businessYear]
  );
  const dateFrom = useMemo(
    () => new Date(businessYearNum, 0, 1, 0, 0, 0, 0),
    [businessYearNum]
  );
  const dateTo = useMemo(
    () => new Date(businessYearNum, 11, 31, 23, 59, 59, 999),
    [businessYearNum]
  );

  const yearOptions = useMemo(() => {
    const years: { id: string; name: string }[] = [];
    for (let y = currentYear - 5; y <= currentYear; y++) {
      years.push({ id: String(y), name: String(y) });
    }
    return years;
  }, [currentYear]);

  const onBusinessYearChange = (_: string, value: string) => {
    setBusinessYear(value);
  };

  const [processing, setProcessing] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [selectedType, setSelectedType] = useState<InventoryType>("mercantile");

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const [wResp, pResp]: [AxiosResponse, AxiosResponse] =
          await Promise.all([getAllWarehouses(), getAllProducts()]);

        ensureSuccess(wResp);
        ensureSuccess(pResp);

        setWarehousesApi(wResp?.data?.result ?? []);
        setProductsApi(pResp?.data?.result ?? []);
      } finally {
        setProcessing(false);
      }
    };
    run();
  }, []);

  const onSelectedWarehouseChange = (_property: string, value: string) => {
    setSelectedWarehouse(value);
  };

  const onSelectedTypeChange = (_property: string, value: InventoryType) => {
    setSelectedType(value);
  };

  const normalizeAmount = (n: number) => (Math.abs(n) < 0.0005 ? 0 : n);

  const calcMercantile = async (): Promise<ExcelRow[]> => {
    const wh = warehousesApi.find((w) => w.id === selectedWarehouse);
    const warehouseName = wh?.name;

    const dateFromTs = dateFrom.getTime();
    const dateToTs = dateTo.getTime();

    const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, dateFrom, dateTo),
        getAllDocuments(true, false, dateFrom, dateTo),
      ]);

    const inputDocs = inputsResp.data.result ?? [];
    const outputDocs = outputsResp.data.result ?? [];

    const nettoMap = new Map<string, number>(); // productId -> sum(netto)
    const srpsMap = new Map<string, number>(); // productId -> sum(srps)

    const applyDoc = (doc: any, sign: 1 | -1) => {
      if (doc?.isDocumentFinished !== true) return;

      const ts = new Date(doc.date).getTime();
      if (ts < dateFromTs || ts > dateToTs) return;

      if (!warehouseName) return;
      if (doc.warehouseName !== warehouseName) return;

      const productId = doc.productId;
      if (!productId) return;

      const key = productId.toString();

      const nettoDelta = (doc.netto ?? 0) * sign;
      nettoMap.set(key, (nettoMap.get(key) ?? 0) + nettoDelta);

      const srpsDelta = (doc.srps ?? 0) * sign;
      srpsMap.set(key, (srpsMap.get(key) ?? 0) + srpsDelta);
    };

    inputDocs.forEach((d: any) => applyDoc(d, 1));
    outputDocs.forEach((d: any) => applyDoc(d, -1));

    const rows: ExcelRow[] = [];
    for (const [prodId, nettoRaw] of Array.from(nettoMap.entries())) {
      const netto = normalizeAmount(nettoRaw);
      const srps = normalizeAmount(srpsMap.get(prodId) ?? 0);

      if (netto <= 0) continue;

      const productMeta = productsApi.find((p) => p.id?.toString() === prodId);
      if (!productMeta?.code) continue;

      rows.push({
        SifraRobe: productMeta.code ?? "",
        Naziv: (productMeta.name as string) ?? "",
        Oznaka: productMeta.measuringUnit ?? "",
        Kolicina: netto,
        Srps: srps,
      });
    }

    return rows.sort((a, b) => {
      const nameA = (a.Naziv ?? "").toLocaleLowerCase();
      const nameB = (b.Naziv ?? "").toLocaleLowerCase();

      const byName = nameA.localeCompare(nameB, "sr");
      if (byName !== 0) return byName;

      return (a.SifraRobe ?? "").localeCompare(b.SifraRobe ?? "", "sr");
    });
  };

  const calcRepromaterial = async (): Promise<ExcelRow[]> => {
    const wh = warehousesApi.find((w) => w.id === selectedWarehouse);
    const warehouseName = wh?.name;

    const dateFromTs = dateFrom.getTime();
    const dateToTs = dateTo.getTime();

    const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(false, true, dateFrom, dateTo),
        getAllDocuments(false, false, dateFrom, dateTo),
      ]);

    const inputDocs = inputsResp.data.result ?? [];
    const outputDocs = outputsResp.data.result ?? [];

    const map = new Map<string, number>(); // productId -> amount

    const processDoc = (doc: any, sign: 1 | -1) => {
      if (doc?.isDocumentFinished !== true) return;

      const ts = new Date(doc.date).getTime();
      if (ts < dateFromTs || ts > dateToTs) return;

      const warehouseMeta = warehousesApi.find((w) => w.id === doc.warehouseId);
      if (warehouseMeta?.virtual === true) return;

      if (!warehouseName) return;
      if (doc.warehouseName !== warehouseName) return;

      const productIds = (doc.reproProducts ?? "")
        .split(";")
        .map((s: string) => s.trim())
        .filter(Boolean);

      const amounts = (doc.reproAmounts ?? "")
        .split(";")
        .map((s: string) => s.trim())
        .filter(Boolean);

      productIds.forEach((prodId: string, idx: number) => {
        const parsed = parseFloat(amounts[idx] ?? "0");
        const delta = (isNaN(parsed) ? 0 : parsed) * sign;

        const key = prodId.toString();
        map.set(key, (map.get(key) ?? 0) + delta);
      });
    };

    inputDocs.forEach((d: any) => processDoc(d, 1));
    outputDocs.forEach((d: any) => processDoc(d, -1));

    const rows: ExcelRow[] = [];
    for (const [prodId, amountRaw] of Array.from(map.entries())) {
      const amount = normalizeAmount(amountRaw);
      if (amount <= 0) continue;

      const productMeta = productsApi.find((p) => p.id?.toString() === prodId);
      if (!productMeta?.code) continue;

      rows.push({
        SifraRobe: productMeta.code ?? "",
        Naziv: (productMeta.name as string) ?? "",
        Oznaka: productMeta.measuringUnit ?? "",
        Kolicina: amount,
      });
    }

    return rows.sort((a, b) => {
      const nameA = (a.Naziv ?? "").toLocaleLowerCase();
      const nameB = (b.Naziv ?? "").toLocaleLowerCase();

      const byName = nameA.localeCompare(nameB, "sr");
      if (byName !== 0) return byName;

      return (a.SifraRobe ?? "").localeCompare(b.SifraRobe ?? "", "sr");
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: string) => {
    import("file-saver").then((module: any) => {
      if (module?.default) {
        const EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const data = new Blob([buffer], { type: EXCEL_TYPE });
        module.default.saveAs(data, fileName + ".xlsx");
      }
    });
  };

  const exportRowsToExcel = async (rows: ExcelRow[]) => {
    const whName =
      warehousesApi.find((w) => w.id === selectedWarehouse)?.name ?? "";
    const typeLabel =
      selectedType === "mercantile" ? "Merkantila" : "Repromaterijal";
    const dateLabel = Utility.formatDate(dateTo);

    const fileName = `PopisnaLista_${whName}_${typeLabel}_${dateLabel}`;

    const xlsx = await import("xlsx");
    const worksheet = xlsx.utils.json_to_sheet(rows);

    // formatiraj numeričke kolone (Kolicina, SRPS)
    if (worksheet["!ref"]) {
      const range = xlsx.utils.decode_range(worksheet["!ref"]);

      const findCol = (headerName: string) => {
        for (let c = range.s.c; c <= range.e.c; c++) {
          const headerAddr = xlsx.utils.encode_cell({ r: 0, c });
          if ((worksheet[headerAddr]?.v ?? "") === headerName) return c;
        }
        return -1;
      };

      const kolicinaCol = findCol("Kolicina");
      const srpsCol = findCol("Srps");

      const formatCol = (col: number) => {
        if (col === -1) return;
        for (let r = range.s.r + 1; r <= range.e.r; r++) {
          const addr = xlsx.utils.encode_cell({ r, c: col });
          const cell = worksheet[addr];
          if (cell && typeof cell.v === "number") {
            cell.t = "n";
            cell.z = Number.isInteger(cell.v) ? "0" : "0.000";
          }
        }
      };

      formatCol(kolicinaCol);
      formatCol(srpsCol);
    }

    const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
    const excelBuffer = xlsx.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    saveAsExcelFile(excelBuffer, fileName);
  };

  const generateInventoryCountList = async () => {
    if (!selectedWarehouse) return;

    setProcessing(true);
    try {
      const rows =
        selectedType === "mercantile"
          ? await calcMercantile()
          : await calcRepromaterial();

      await exportRowsToExcel(rows);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <PageLayout>
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-clipboard-check header-title-icon"
              style={{ fontSize: 16 }}
            ></i>
            <span className="header-title-left">{t("Popisne liste")}</span>
          </div>
        </div>
        <div className="form-body">
          <DropdownInput
            id="businessyear"
            value={businessYear}
            onValueChange={onBusinessYearChange}
            optionValue="id"
            optionLabel="name"
            options={yearOptions}
            filter={false}
            labelText={t("Popisna lista za poslovnu godinu *")}
            placeholderText={t("Izaberite godinu")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
            disabled={processing}
          />
          <DropdownInput
            id="selectedWarehouse"
            value={selectedWarehouse}
            onValueChange={onSelectedWarehouseChange}
            optionValue="id"
            optionLabel="name"
            options={warehousesApi.filter((w) => w.virtual !== true)}
            filter={false}
            labelText={t("Magacin *")}
            placeholderText={t("Izaberite iz liste")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
            disabled={processing}
          />
          <DropdownInput
            id="selectedType"
            value={selectedType}
            onValueChange={onSelectedTypeChange as any}
            optionValue="id"
            optionLabel="name"
            options={[
              { id: "mercantile", name: t("Merkantila") },
              { id: "repromaterial", name: t("Repromaterijal") },
            ]}
            filter={false}
            labelText={t("Tip popisa *")}
            placeholderText={t("Izaberite iz liste")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
            disabled={processing}
          />
          <div className="footer-buttons">
            {processing ? (
              <ProgressSpinner
                style={{ width: "70px", height: "70px", color: "green" }}
                strokeWidth="4"
                animationDuration=".8s"
              />
            ) : (
              <ButtonInput
                labelText={t("Generiši popisnu listu")}
                icon="bi bi-file-earmark-excel"
                onButtonClick={generateInventoryCountList}
                disabled={!selectedWarehouse || !selectedType || processing}
                addStyle={{
                  background: "rgba(0, 165, 83, 1)",
                  border: "none",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
